// MSAL provider boot. Wraps the app; provides MsalContext used by useAccessToken.
//
// 2026-05-04 fix: the prior implementation used a module-global ``initialized``
// flag flipped from inside ``initPromise.then(...)``. Because nothing inside
// React subscribed to that flag, the component never re-rendered after the
// MSAL handshake completed — leaving the page stuck on "Initializing…" the
// first time the redirect flow succeeded. The same bug existed in admin-ui;
// admin-ui live validation surfaced it on first authenticated load.
//
// The fix uses React state + useEffect so the post-resolution flip triggers
// a re-render. Adds an error branch so the user knows when MSAL init failed
// instead of seeing an infinite spinner.
//
// 2026-05-21 stale-state recovery (mirrors admin-ui fix):
// Production report on admin-ui: after the user was logged in for some time
// and then F5'd, the app briefly redirected to login.microsoftonline.com
// and Microsoft returned "We couldn't sign you in. Please try again." The
// user had to manually clear cookies to recover. chat-ui carries the same
// MSAL singleton + handleRedirectPromise pattern, so the same failure mode
// applies: when the Entra SSO session cookie expires while the local MSAL
// sessionStorage still has cached state, an interactive redirect can land
// in AAD's "couldn't sign you in" dead-end. Applying the same self-heal
// here keeps the two UIs in sync.
//
// Recovery path: detect either (a) ``error=`` returned in the URL after a
// failed AAD interaction, or (b) ``handleRedirectPromise()`` rejecting
// with a known recoverable code (invalid_state, interaction_in_progress,
// etc.) — then clear the MSAL cache and force a fresh login with
// ``prompt:'select_account'`` to bypass the stale SSO state. A per-session
// guard prevents infinite recovery loops.

import { type AccountInfo, BrowserAuthError, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { type ReactNode, useEffect, useState } from "react";
import { AuthScreen } from "./AuthScreen";
import { loginRequest, msalConfig } from "./msalConfig";

// Singleton — MSAL re-instantiation is expensive and breaks token cache.
const pca = new PublicClientApplication(msalConfig);

// MSAL needs an explicit initialization step before any API call. Kicked
// off at module load; the promise is shared across mounts so React.StrictMode's
// double-invoke doesn't trigger two redirects.
const initPromise = pca.initialize().then(() => pca.handleRedirectPromise());

type InitState = "pending" | "ready" | "error";

// Per-session marker set when a recovery attempt is dispatched. Cleared
// once the recovered session reaches a fully-ready state. Prevents
// infinite-redirect loops if the recovery itself encounters another
// corrupt-state error.
const RECOVERY_GUARD_KEY = "chat_ui_msal_recovery_in_flight_v1";

// BrowserAuthError codes from MSAL v5 where the local temp cache is
// corrupted but a fresh interactive login WILL recover. Mirrors the
// admin-ui list; keep the two in sync.
const RECOVERABLE_INIT_ERROR_CODES = new Set<string>([
  "invalid_state",
  "unable_to_parse_state",
  "state_interaction_type_mismatch",
  "interaction_in_progress",
  "no_cached_authority_error",
  "invalid_grant",
  "hash_empty_error",
  "endpoints_resolution_error",
]);

/**
 * Pull an OAuth2 error returned by AAD from a URL.
 *
 * AAD's redirect-back-with-error pattern lands the error in EITHER the
 * URL fragment (``#error=...&error_description=...``) or the query string
 * (``?error=...&error_description=...``), depending on response_mode.
 * Check both so the recovery path is mode-agnostic.
 */
function extractAadErrorFromUrl(href: string): { code: string; description: string } | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const rawHash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const hashParams = new URLSearchParams(rawHash);
  const code = hashParams.get("error") ?? url.searchParams.get("error");
  if (!code) return null;
  return {
    code,
    description: hashParams.get("error_description") ?? url.searchParams.get("error_description") ?? "",
  };
}

function getErrorCode(err: unknown): string {
  if (err instanceof BrowserAuthError) return err.errorCode;
  if (err && typeof err === "object" && "errorCode" in err) {
    const v = (err as { errorCode: unknown }).errorCode;
    return typeof v === "string" ? v : "";
  }
  return "";
}

/**
 * Self-heal from a known-bad MSAL/Entra state by wiping local cache and
 * forcing a fresh interactive login that bypasses SSO.
 */
async function recoverFromCorruptedState(reason: string): Promise<never> {
  const prior = sessionStorage.getItem(RECOVERY_GUARD_KEY);
  if (prior) {
    sessionStorage.removeItem(RECOVERY_GUARD_KEY);
    throw new Error(`MSAL recovery already attempted (prior=${prior}, current=${reason}); failing to avoid loop`);
  }
  sessionStorage.setItem(RECOVERY_GUARD_KEY, reason);

  try {
    await pca.clearCache();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[chat-ui] MSAL clearCache failed during recovery (continuing):", err);
  }

  // eslint-disable-next-line no-console
  console.warn(`[chat-ui] MSAL stale-state recovery dispatched (reason=${reason})`);
  await pca.loginRedirect({ ...loginRequest, prompt: "select_account" });
  throw new Error("loginRedirect did not navigate");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<InitState>("pending");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // ── 0. AAD error returned in the URL ────────────────────────
        const aadError = extractAadErrorFromUrl(window.location.href);
        if (aadError) {
          // eslint-disable-next-line no-console
          console.warn("[chat-ui] AAD returned error in URL:", aadError);
          const url = new URL(window.location.href);
          url.hash = "";
          url.search = "";
          window.history.replaceState(null, "", url.pathname);
          await recoverFromCorruptedState(`aad-${aadError.code}`);
          return; // navigating away
        }

        await initPromise;
        if (cancelled) return;

        // F16/P0-3 fix: align with admin-ui — drive login from
        // AuthProvider, not from useAccessToken. Prior chat-ui-only
        // divergence let MsalProvider mount children while accounts
        // was still empty; multiple components (Composer +
        // ConversationSidebar + TenantSwitcher) then each called
        // ``acquireToken`` on first render, racing to issue
        // ``loginRedirect`` with the API-only scope set (missing
        // ``openid/profile/email`` from ``loginRequest``). The race
        // produced ``interaction_in_progress`` + AAD bounced back to
        // the email entry page rather than chat-ui.
        const accounts: AccountInfo[] = pca.getAllAccounts();
        if (accounts.length === 0) {
          await pca.loginRedirect(loginRequest);
          return;
        }
        if (!pca.getActiveAccount()) {
          pca.setActiveAccount(accounts[0]);
        }

        sessionStorage.removeItem(RECOVERY_GUARD_KEY);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;

        // ── 1. Recoverable init error ───────────────────────────────
        const errorCode = getErrorCode(err);
        if (RECOVERABLE_INIT_ERROR_CODES.has(errorCode)) {
          try {
            await recoverFromCorruptedState(`init-${errorCode}`);
            return; // navigating away
          } catch (recoveryErr) {
            // eslint-disable-next-line no-console
            console.error("[chat-ui] MSAL recovery failed:", recoveryErr);
            const recoveryMsg = recoveryErr instanceof Error ? recoveryErr.message : String(recoveryErr);
            const initMsg = err instanceof Error ? err.message : String(err);
            setError(`${initMsg} — recovery: ${recoveryMsg}`);
            setStatus("error");
            return;
          }
        }

        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setStatus("error");
        // eslint-disable-next-line no-console
        console.error("[chat-ui] MSAL init failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "pending") {
    return <AuthScreen variant="loading" />;
  }
  if (status === "error") {
    return <AuthScreen variant="error" message={error} />;
  }
  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}

export { pca };
